import React, { useEffect } from 'react';
import { Form, Formik, FormikHelpers } from 'formik';
import { useLocation, useNavigate } from 'react-router-dom';
import * as Yup from 'yup';
import {
    getErrorMessage,
    hasDigit,
    hasLowerCase,
    hasSymbol,
    hasUpperCase,
    openNotification,
    validatePassword
} from '../utils/helper';
import Passwordinput from '../components/passwordinput/passwordinput';
import { logoutUser } from '../store/userSlice/userSlice';
import { useDispatch } from 'react-redux';
import { mutateConfig } from '../api/queryBase';
import { AxiosError } from 'axios';
import Button from '../components/button/Button';
import { FormControlLabel } from '@mui/material';
import { Checkbox } from 'antd';

const InvitedUserPassword = () => {
    interface InvitePasswordValues {
        email: string | null;
        password: string;
        confirmPassword: string;
        inviteCode: string | null;
    }
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const location = useLocation();
    const query = new URLSearchParams(location.search);
    const mutation = mutateConfig();
    const { mutate: mutateAcceptInvite } = mutation({
        url: `users/invite/accept`
    });

    const initialValues: InvitePasswordValues = {
        email: query.get('email')?.replace(/\s/g, '+') || '',
        password: '',
        confirmPassword: '',
        inviteCode: query.get('code')
    };

    const validationSchema = Yup.object().shape({
        password: Yup.string()
            .required('Password is required')
            .test('password', 'Invalid password', (value) =>
                validatePassword(value)
            ),
        confirmPassword: Yup.string().oneOf(
            [Yup.ref('password'), ''],
            'Passwords do not match'
        )
    });

    const handleSubmit = async (
        values: InvitePasswordValues,
        { setSubmitting }: FormikHelpers<InvitePasswordValues>
    ) => {
        setSubmitting(true);
        const { email, inviteCode, password } = values;

        mutateAcceptInvite(
            { email, inviteCode, password },
            {
                onSettled: (_, error: AxiosError | undefined | any) => {
                    if (error) {
                        setSubmitting(false);

                        openNotification(
                            'error',
                            getErrorMessage(error) || 'Something went wrong'
                        );
                        return;
                    }
                    setSubmitting(false);
                },
                onSuccess: () => {
                    setSubmitting(false);

                    openNotification(
                        'success',
                        'Password was updated successfully was successful.'
                    );

                    navigate('/login');
                }
            }
        );
    };

    useEffect(() => {
        dispatch(logoutUser());
    }, []);

    return (
        <div>
            <div className='bg-appcolorwhite py-[2.7rem] lg:py-[2.5rem] lg:px-[2.6rem] px-[1.6rem] mb-[1.75rem] rounded-[3px]'>
                <Formik
                    initialValues={initialValues}
                    validationSchema={validationSchema}
                    onSubmit={handleSubmit}
                >
                    {({
                        errors,
                        isValid,
                        values,
                        setFieldValue,
                        touched,
                        isSubmitting
                    }) => (
                        <Form autoComplete='off' noValidate>
                            <Passwordinput
                                id='password'
                                label='Password'
                                name='password'
                                updateField={setFieldValue}
                                showToggle={true}
                                error={touched?.password && !!errors?.password}
                                helperText={
                                    touched?.password
                                        ? errors?.password
                                        : undefined
                                }
                            />
                            {values.password && (
                                <div className='flex flex-wrap gap-1 px-4 mt-1'>
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                className='text-xs'
                                                checked={Boolean(
                                                    values?.password &&
                                                        values?.password
                                                            ?.length > 7
                                                )}
                                            />
                                        }
                                        label={
                                            <p className='text-xs ml-2'>
                                                Minimum 8 characters
                                            </p>
                                        }
                                    />
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                checked={Boolean(
                                                    values?.password &&
                                                        hasSymbol(
                                                            values?.password
                                                        )
                                                )}
                                            />
                                        }
                                        label={
                                            <p className='text-xs ml-2'>
                                                At least 1 symbol
                                            </p>
                                        }
                                    />
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                checked={Boolean(
                                                    values?.password &&
                                                        hasDigit(
                                                            values?.password
                                                        )
                                                )}
                                            />
                                        }
                                        label={
                                            <p className='text-xs ml-2'>
                                                At least 1 number
                                            </p>
                                        }
                                    />
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                checked={Boolean(
                                                    values?.password &&
                                                        hasLowerCase(
                                                            values?.password
                                                        )
                                                )}
                                            />
                                        }
                                        label={
                                            <p className='text-xs ml-2'>
                                                At least 1 lower case letter
                                            </p>
                                        }
                                    />
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                checked={Boolean(
                                                    values?.password &&
                                                        hasUpperCase(
                                                            values?.password
                                                        )
                                                )}
                                            />
                                        }
                                        label={
                                            <p className='text-xs ml-2'>
                                                At least 1 upper case letter
                                            </p>
                                        }
                                    />
                                </div>
                            )}
                            <div className='mt-4'>
                                <Passwordinput
                                    id='confirmPassword'
                                    label='Re-enter Password'
                                    name='confirmPassword'
                                    showToggle={true}
                                    updateField={setFieldValue}
                                    error={
                                        touched?.confirmPassword &&
                                        !!errors?.confirmPassword
                                    }
                                    helperText={
                                        touched?.confirmPassword
                                            ? errors?.confirmPassword
                                            : undefined
                                    }
                                />
                            </div>

                            <div className='mt-4'>
                                <Button
                                    isLoading={isSubmitting}
                                    type='submit'
                                    className='w-full py-7'
                                    disabled={
                                        !isValid ||
                                        isSubmitting ||
                                        !values.password ||
                                        !values.confirmPassword
                                    }
                                    text='Continue'
                                />
                            </div>
                        </Form>
                    )}
                </Formik>
            </div>
        </div>
    );
};

export default InvitedUserPassword;
